import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { UserProvider } from "./Common/UserContext";
import { MenuProvider } from "./Common/MenuContext";
import { MenuStateProvider } from "./Common/MenuStateContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppAlertHost from "./components/AppAlert/AppAlertHost";
import "./assets/css/variables.css";
import MainLayout from "./pages/main_layout";
import ReImagineLite from "./pages/ReImagineLite";
import SignIn from "./pages/signin";
import SignUp from "./pages/signup";
import ProfileDetails from "./pages/profile";
import ProfileDetailsu from "./pages/su/profile";
import OTPVerification from "./pages/otp";
import OTPVerificationSu from "./pages/su/otp";
import Projects from "./pages/project";
import Projectsu from "./pages/su/project";
import Dashboard from "./pages/dashboard";
import Dashboardsu from "./pages/su/dashboard";
import DashboardSa from "./pages/sa/dashboard";
import Capabilities from "./pages/capablities";
import Reports from "./pages/reports";
import Reportsu from "./pages/su/reports";
import Assessment from "./pages/assessment";
import ImportExport from "./pages/importExport";
import Chat from "./pages/chat";
import ForgotPassword from "./pages/forgotpassword";
import ChangePassword from "./pages/change-password";
import ResetPassword from "./pages/resetpassword";
import TeamManagement from "./pages/su/team_member";
import PrivacyPolicy from "./pages/privacy_policy";
import TermsAndConditions from "./pages/terms_condition";
import { useAuth } from "./auth/AuthContext";
import CapAssess from './pages/su/cap_assess';
import Role from './pages/sa/role';
import User from './pages/sa/user_management';
import RightsManagement from './pages/sa/rights_management';
import CompanyManagement from './pages/sa/company_management';
import BusinessTypeManagement from './pages/sa/business_type';
import SaSettings from './pages/sa/settings';
import ProjectManagement from './pages/sa/project_management';
import CapabilityManagement from "./pages/sa/capability_management";
import CapabilityFormPage from "./pages/sa/CapabilityFormPage";
import IndustryManagement from "./pages/sa/IndustryManagement";
import FrameworkManagement from "./pages/sa/FrameworkManagement";
import MasterList from "./pages/sa/MasterList";
import TraxccelFrameworks from "./pages/sa/TraxccelFrameworks";
import AssessmentBlueprints from "./pages/sa/AssessmentBlueprints";
import AssessmentBlueprintWizard from "./pages/sa/AssessmentBlueprintWizard";
import SectorManagement from "./pages/sa/SectorManagement";
import FunctionsManagement from "./pages/sa/FunctionsManagement";
import DataManagement from "./pages/sa/DataManagement";
import CapabilityApproval from "./pages/sa/capability_approval";
import ProjectSetupApproval from "./pages/sa/project_setup_approval";
import ProjectAssessments from "./pages/sa/ProjectAssessments";
import ProjectChats from "./pages/sa/ProjectChats";
import ProjectReports from "./pages/sa/ProjectReports";
import RequestProject from "./pages/request_project";
import BusinessValueDrivers from "./pages/sa/BusinessValueDrivers";
import FunctionalValueDrivers from "./pages/sa/FunctionalValueDrivers";
import UseCases from "./pages/sa/UseCases";
import AssessmentTemplateManagement from "./pages/sa/assessment_template_management";
import SMEDashboard from "./pages/sme/dashboard";
import SMETracking from "./pages/sme/assesstracking";
import SmeProjectReports from "./pages/sme/projectReports";
import ValueEstimation from "./pages/ValueEstimation";
import BusinessValueCalculator from "./pages/BusinessValueCalculator";
import Roadmap from "./pages/roadmap";
import AdminDashboard from "./pages/admin/dashboard";
import { isAdminUser } from "./auth/roleUtils";
import "./assets/css/content-loader.css";
import "./assets/css/dashboard-toast.css";

const basename = process.env.PUBLIC_URL || "";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

/** SuperAdmin dashboard — Admin users are sent to their own dashboard instead. */
const SaDashboardEntry = () => {
  const { user } = useAuth();
  if (isAdminUser(user)) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return <DashboardSa />;
};

const ProtectedRoute = ({ children, requireAuth = true }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="app-loading" role="status" aria-live="polite">
        <div className="content-loader__spinner" aria-hidden="true" />
        <p className="content-loader__label">Loading…</p>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="app-loading" role="status" aria-live="polite">
        <div className="content-loader__spinner" aria-hidden="true" />
        <p className="content-loader__label">Loading…</p>
      </div>
    );
  }

  return (
          <Routes>
          <Route path="/" element={<MainLayout />} />
          <Route path="/main_layout" element={<MainLayout />} />
          <Route path="/reimagine-lite" element={<ReImagineLite />} />
          <Route path="/reimagine-lite/roadmap" element={<Roadmap />} />
          
          {/* SignIn Routes — always render SignIn; it handles post-login navigation */}
          <Route path="/signin" element={<SignIn />} />
          
          {/* SignUp — open client signup at /signup, or project invite via ?invite= */}
          <Route
            path="/signup"
            element={!isAuthenticated ? <SignUp /> : <Navigate to="/signin" replace />}
          />

          <Route
            path="/request-project"
            element={!isAuthenticated ? <RequestProject /> : <Navigate to="/signin" replace />}
          />
          
          {/* Profile Routes */}
          <Route
            path="/profile/:id"
            element={!isAuthenticated ? <ProfileDetails /> : <Navigate to="/signin" replace />}
          />
          <Route
            path="/su/profile/:id"
            element={!isAuthenticated ? <ProfileDetailsu /> : <Navigate to="/signin" replace />}
          />
          
          {/* OTP Routes */}
          <Route path="/otp" element={<OTPVerification />} />
          <Route path="/su/otp" element={<OTPVerificationSu />} />
          
          {/* SuperUser (SU) Routes */}
          <Route 
            path="/su/team_member" 
            element={
              <ProtectedRoute>
                <TeamManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/su/cap_assess" 
            element={
              <ProtectedRoute>
                <CapAssess />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/su/project"
            element={
              <ProtectedRoute>
                <Projectsu />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/su/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboardsu />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/su/reports" 
            element={
              <ProtectedRoute>
                <Reportsu />
              </ProtectedRoute>
            }
          />

          {/* SME Routes */}
          <Route
            path="/sme/dashboard"
            element={
              <ProtectedRoute>
                <SMEDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sme/assesstracking"
            element={
              <ProtectedRoute>
                <SMETracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sme/project-reports"
            element={
              <ProtectedRoute>
                <SmeProjectReports />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Super Admin (SA) Routes */}
          <Route 
            path="/sa/role" 
            element={
              <ProtectedRoute>
                <Role />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/user_management" 
            element={
              <ProtectedRoute>
                <User />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/rights_management" 
            element={
              <ProtectedRoute>
                <RightsManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/company_management" 
            element={
              <ProtectedRoute>
                <CompanyManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/business_type" 
            element={
              <ProtectedRoute>
                <BusinessTypeManagement />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/sa/settings"
            element={
              <ProtectedRoute>
                <SaSettings />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/sa/project_management" 
            element={
              <ProtectedRoute>
                <ProjectManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/capability_management/new"
            element={
              <ProtectedRoute>
                <CapabilityFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/capability_management/:capabilityId/edit"
            element={
              <ProtectedRoute>
                <CapabilityFormPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/sa/capability_management" 
            element={
              <ProtectedRoute>
                <CapabilityManagement />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/sa/IndustryManagement"
            element={
              <ProtectedRoute>
                <IndustryManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/FrameworkManagement"
            element={
              <ProtectedRoute>
                <FrameworkManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/MasterList"
            element={
              <ProtectedRoute>
                <MasterList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/TraxccelFrameworks"
            element={
              <ProtectedRoute>
                <TraxccelFrameworks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/AssessmentBlueprints"
            element={
              <ProtectedRoute>
                <AssessmentBlueprints />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/AssessmentBlueprints/new"
            element={
              <ProtectedRoute>
                <AssessmentBlueprintWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/AssessmentBlueprints/:id"
            element={
              <ProtectedRoute>
                <AssessmentBlueprintWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/SectorManagement"
            element={
              <ProtectedRoute>
                <SectorManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/FunctionsManagement" 
            element={
              <ProtectedRoute>
                <FunctionsManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/DataManagement" 
            element={
              <ProtectedRoute>
                <DataManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/capability_approval" 
            element={
              <ProtectedRoute>
                <CapabilityApproval />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/sa/project_setup_approval"
            element={
              <ProtectedRoute>
                <ProjectSetupApproval />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/project_assessments"
            element={
              <ProtectedRoute>
                <ProjectAssessments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/project_chats"
            element={
              <ProtectedRoute>
                <ProjectChats />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sa/project_reports"
            element={
              <ProtectedRoute>
                <ProjectReports />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/sa/BusinessValueDrivers" 
            element={
              <ProtectedRoute>
                <BusinessValueDrivers />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/FunctionalValueDrivers" 
            element={
              <ProtectedRoute>
                <FunctionalValueDrivers />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sa/UseCases" 
            element={
              <ProtectedRoute>
                <UseCases />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/sa/assessment_template_management"
            element={
              <ProtectedRoute>
                <AssessmentTemplateManagement />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/sa/dashboard" 
            element={
              <ProtectedRoute>
                <SaDashboardEntry />
              </ProtectedRoute>
            } 
          />
          
          {/* Client/User Routes */}
          <Route
            path="/project"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/capabilities"
            element={
              <ProtectedRoute>
                <Capabilities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment"
            element={
              <ProtectedRoute>
                <UserProvider>
                  <Assessment />
                </UserProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/import-export"
            element={
              <ProtectedRoute>
                <ImportExport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/value-estimation"
            element={
              <ProtectedRoute>
                <ValueEstimation />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/BusinessValueCalculator"
            element={
              <ProtectedRoute>
                <BusinessValueCalculator />
              </ProtectedRoute>
            }
          />
          
          {/* Public Routes */}
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/privacy_policy" element={<PrivacyPolicy />} />
          <Route path="/terms_condition" element={<TermsAndConditions />} />
          
          {/* Change Password - Available to all authenticated users */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />

          {/* Avoid a blank white page when a path does not match any route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <Router basename={basename}>
      <MenuProvider>
        <MenuStateProvider>
          <AppRoutes />
        </MenuStateProvider>
      </MenuProvider>
      
      {/* Global ToastContainer - Single instance for the entire app */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Global themed alert modal — replaces native alert() in the client portal */}
      <AppAlertHost />
    </Router>
  );
}

export default App;